import React from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';

/**
 * Error boundary specifically for handling orphaned annotations (404 errors)
 * When an annotation mark exists in the document but the annotation record
 * is missing from the database, this boundary catches the error and shows
 * a user-friendly toast notification instead of crashing the UI.
 * 
 * Note: We intentionally show a message rather than hiding, to maintain
 * transparency with users about sync issues and provide clear next steps.
 */
export default class AnnotationErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false,
      isOrphanedAnnotation: false
    };
  }

  static getDerivedStateFromError(error) {
    // Check if this is a 404 error from rest-hooks (orphaned annotation)
    const isOrphanedAnnotation = 
      (error && error.status === 404) || 
      (error && error.response && error.response.status === 404) ||
      (error && error.message && error.message.includes('404'));

    return {
      hasError: true,
      isOrphanedAnnotation
    };
  }

  componentDidCatch(error, errorInfo) {
    const { markId } = this.props;
    
    // If this is an orphaned annotation (404), handle it gracefully
    if (this.state.isOrphanedAnnotation) {
      console.warn(
        `Orphaned annotation detected: ${markId}. ` +
        'The annotation mark exists in the document but the annotation record is missing from the database.',
        { error, errorInfo }
      );
      
      // Show toast notification to user
      toast.warning(
        'Comment could not be loaded. It may have been removed or there was a sync issue.',
        {
          toastId: `orphaned-annotation-${markId}`,
          autoClose: 8000
        }
      );
      
      // Don't call onOrphanedAnnotation - let the user see where the broken comment is
      // so they can navigate to it and replace it
    } else {
      // For other errors, log them normally
      console.error('Error in annotation component:', error, errorInfo);
    }
  }

  render() {
    const { hasError, isOrphanedAnnotation } = this.state;
    const { children, fallback } = this.props;

    if (hasError && isOrphanedAnnotation) {
      // For orphaned annotations, toast is already shown in componentDidCatch
      // Return null to hide the comment panel since the toast provides the notification
      return fallback || null;
    }

    if (hasError) {
      // For other errors, show error message
      return (
        <div style={{ padding: '10px', color: '#e74c3c' }}>
          <p>Unable to load annotation. Please refresh the page.</p>
        </div>
      );
    }

    return children;
  }
}

AnnotationErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  markId: PropTypes.string.isRequired,
  onOrphanedAnnotation: PropTypes.func,
  fallback: PropTypes.node
};

AnnotationErrorBoundary.defaultProps = {
  onOrphanedAnnotation: null,
  fallback: null
};




// WEBPACK FOOTER //
// ./app/javascript/client/components/textEditor/AnnotationErrorBoundary.jsx